// pages/GrammarQuizExercise.jsx
// Page d'exercice Quiz Grammaire (QCM sur des points de grammaire)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Brain, Loader2, CheckCircle, XCircle, TrendingUp, Play, RotateCw, ChevronRight, BookOpen, HelpCircle, Clock, X } from 'lucide-react';

import { getLangueFlag } from '../utils/exerciseUtils';
import { useAuth } from '../contexts/AuthContext';
import { useLangueEtudiee } from '../hooks/useLangueEtudiee';
import { useLangueInterface } from '../contexts/LangueInterfaceContext';
import './ExerciseShared.css';
import './GrammarQuizExercise.css';

export default function GrammarQuizExercise() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const userId = user?.id || 1; // Fallback userId=1 pour embed sans auth
  const langueEtudiee = useLangueEtudiee();
  const { langueInterface } = useLangueInterface();
  const isEmbed = searchParams.get('embed') === 'true';

  // ========== ÉTATS ==========
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Paramètres (écran 1) — langueEtudiee (param WP) prioritaire sur la préférence sauvegardée
  const savedParams = JSON.parse(localStorage.getItem('grammarQuizParams') || '{}');
  const [langue, setLangue] = useState(langueEtudiee || savedParams.langue || 'en');
  const [quantite, setQuantite] = useState(savedParams.quantite || 10);
  const [niveau, setNiveau] = useState(savedParams.niveau || 'all');
  const [generiqueId, setGeneriqueId] = useState(savedParams.generiqueId || 'all');
  const [generiques, setGeneriques] = useState([]);
  const [loadingGeneriques, setLoadingGeneriques] = useState(false);
  const [niveauCounts, setNiveauCounts] = useState({});

  // Exercice (écran 2)
  const [showExercise, setShowExercise] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [result, setResult] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [stats, setStats] = useState({ correct: 0, incorrect: 0, total: 0 });
  const [score, setScore] = useState(0);
  const [showRule, setShowRule] = useState(false);

  // Pool de retry (ref pour accès synchrone, state pour affichage UI)
  const retryQueueRef = useRef([]);
  const [retryQueueLength, setRetryQueueLength] = useState(0);
  // Set des IDs de questions ratées (session + BDD) pour savoir si on doit mémoriser une bonne réponse
  const failedQuestionIdsRef = useRef(new Set());

  // Tracking "Je ne sais pas" — scoring neutre mais comptabilisé
  const hintUsedRef = useRef(new Set());          // Set<questionId> — questions ayant eu un hint
  const [hintCount, setHintCount] = useState(0);  // Compteur affiché en orange

  // Compteur retry local par question (Map<questionId, number>)
  const retryCountersRef = useRef(new Map());

  // Modale "Je ne sais pas"
  const [showHintModal, setShowHintModal] = useState(false);
  const [hintData, setHintData] = useState(null);
  const [loadingHint, setLoadingHint] = useState(false);

  // Chronomètre
  const [chrono, setChrono] = useState(0);
  const chronoRef = useRef(null);

  // Écran fin
  const [quizFinished, setQuizFinished] = useState(false);

  // ========== CHRONOMÈTRE ==========
  const startChrono = useCallback(() => {
    chronoRef.current = setInterval(() => {
      setChrono(prev => prev + 1);
    }, 1000);
  }, []);

  const stopChrono = useCallback(() => {
    if (chronoRef.current) {
      clearInterval(chronoRef.current);
      chronoRef.current = null;
    }
  }, []);

  const formatChrono = (seconds) => {
    const min = Math.floor(seconds / 60).toString().padStart(2, '0');
    const sec = (seconds % 60).toString().padStart(2, '0');
    return `${min}:${sec}`;
  };

  // Cleanup chrono au démontage
  useEffect(() => {
    return () => stopChrono();
  }, [stopChrono]);

  // Réinitialiser l'écran 1 quand on navigue vers cette page depuis le menu
  useEffect(() => {
    if (location.state?.resetToScreen1) {
      stopChrono();
      setShowExercise(false);
      setQuestions([]);
      setCurrentIndex(0);
      setResult(null);
      setStats({ correct: 0, incorrect: 0, total: 0 });
      setScore(0);
      setQuizFinished(false);
      retryQueueRef.current = [];
      setRetryQueueLength(0);
      failedQuestionIdsRef.current = new Set();
      setChrono(0);
    }
  }, [location.state, stopChrono]);

  // Sauvegarder les paramètres dans localStorage
  useEffect(() => {
    localStorage.setItem('grammarQuizParams', JSON.stringify({ langue, quantite, niveau, generiqueId }));
  }, [langue, quantite, niveau, generiqueId]);

  // Charger les génériques quand la langue change
  useEffect(() => {
    const chargerGeneriques = async () => {
      setLoadingGeneriques(true);
      setGeneriqueId('all');
      try {
        const response = await fetch(`/api/exercices/quiz-grammaire/generiques?langue=${langue}&langueExplication=${langueInterface}`);
        const data = await response.json();
        if (data.success) {
          setGeneriques(data.data);
        } else {
          setGeneriques([]);
        }
      } catch (err) {
        console.error('Erreur chargement génériques:', err);
        setGeneriques([]);
      } finally {
        setLoadingGeneriques(false);
      }
    };
    chargerGeneriques();
  }, [langue, langueInterface]);

  // Charger le nombre de questions par niveau quand langue ou generiqueId change
  useEffect(() => {
    const chargerCounts = async () => {
      try {
        const params = new URLSearchParams({ langue, langueExplication: langueInterface });
        if (generiqueId !== 'all') params.append('generiqueId', generiqueId);
        const response = await fetch(`/api/exercices/quiz-grammaire/questions/count?${params}`);
        const data = await response.json();
        if (data.success) {
          setNiveauCounts(data.data);
        }
      } catch (err) {
        console.error('Erreur chargement counts par niveau:', err);
      }
    };
    chargerCounts();
  }, [langue, generiqueId, langueInterface]);

  // Options de langue
  const langueOptions = [
    { value: 'fr', label: t('langues.fr'), flag: '🇫🇷' },
    { value: 'en', label: t('langues.en'), flag: '🇬🇧' },
    { value: 'es', label: t('langues.es'), flag: '🇪🇸' },
  ];

  // Options de niveau
  const niveauOptions = [
    { value: 'all', label: t('grammarQuiz.niveauAll') },
    { value: 'beginner', label: t('grammarQuiz.niveauBeginner') },
    { value: 'intermediate', label: t('grammarQuiz.niveauIntermediate') },
    { value: 'advanced', label: t('grammarQuiz.niveauAdvanced') },
  ];

  // Options de quantité
  const quantiteOptions = [
    { value: 10, label: t('grammarQuiz.questionsCount', { count: 10 }) },
    { value: 20, label: t('grammarQuiz.questionsCount', { count: 20 }) },
    { value: 50, label: t('grammarQuiz.questionsCount', { count: 50 }) },
  ];

  // ========== POOL DE RETRY ==========
  const addToRetryQueue = (questionData) => {
    const questionsUntilRetry = Math.floor(Math.random() * 3) + 3; // 3-5 questions
    retryQueueRef.current = [...retryQueueRef.current, { questionsUntilRetry, questionData }];
    setRetryQueueLength(retryQueueRef.current.length);
  };

  const processRetryQueue = () => {
    const updated = retryQueueRef.current.map(item => ({
      ...item,
      questionsUntilRetry: item.questionsUntilRetry - 1,
    }));
    const readyIndex = updated.findIndex(item => item.questionsUntilRetry <= 0);
    if (readyIndex !== -1) {
      const readyQuestion = updated[readyIndex].questionData;
      retryQueueRef.current = updated.filter((_, i) => i !== readyIndex);
      setRetryQueueLength(retryQueueRef.current.length);
      return readyQuestion;
    }
    retryQueueRef.current = updated;
    setRetryQueueLength(updated.length);
    return null;
  };

  // ========== MÉMORISATION BDD ==========
  const updateScoreBDD = async (questionId, eventType) => {
    // eventType: 'correct', 'incorrect', 'hint'
    if (!userId) return;
    try {
      await fetch('/api/exercices/scores/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: userId,
          typeExercice: 'quiz-grammaire',
          questionRef: `quiz:${questionId}`,
          eventType,
          niveauExercice: niveau !== 'all' ? niveau : null,
          langue,
        }),
      });
    } catch (err) {
      console.error('Erreur update score BDD:', err);
    }
  };

  const saveSessionEnd = async (finalStats, finalScore, finalChrono, finalHintCount) => {
    if (!userId) return;
    try {
      await fetch('/api/exercices/scores/session-end', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: userId,
          typeExercice: 'quiz-grammaire',
          niveauExercice: niveau !== 'all' ? niveau : null,
          langue,
          nbQuestions: finalStats.total,
          nbCorrect: finalStats.correct,
          nbIncorrect: finalStats.incorrect,
          nbHint: finalHintCount,
          score: finalScore,
          durationSeconds: finalChrono,
        }),
      });
    } catch (err) {
      console.error('Erreur session-end:', err);
    }
  };

  // ========== FONCTIONS ==========

  const startQuiz = async () => {
    setLoading(true);
    setError(null);
    try {
      // Charger les questions retry de la BDD (compteur < 1)
      let retryQuestionIds = [];
      let retryScoresMap = new Map(); // Map<questionId, score> pour initialiser les compteurs
      if (user?.id) {
        try {
          const retryRes = await fetch(`/api/exercices/scores/retry?userId=${userId}&typeExercice=quiz-grammaire&limit=10`);
          const retryData = await retryRes.json();
          if (retryData.success && retryData.data.length > 0) {
            retryData.data.forEach(r => {
              const id = parseInt(r.questionRef.replace('quiz:', ''));
              if (!isNaN(id)) {
                retryQuestionIds.push(id);
                retryScoresMap.set(id, r.score);
              }
            });
          }
        } catch (err) {
          console.error('Erreur chargement retry:', err);
        }
      }

      const params = new URLSearchParams({
        langue,
        quantite: quantite.toString(),
        langueExplication: langueInterface,
      });
      if (niveau !== 'all') params.append('niveau', niveau);
      if (generiqueId !== 'all') params.append('generiqueId', generiqueId);

      const response = await fetch(`/api/exercices/quiz-grammaire/questions?${params}`);
      const data = await response.json();

      if (data.success && data.data.questions.length > 0) {
        let allQuestions = data.data.questions;

        // Alternance 1:1 — retry/nouvelle
        if (retryQuestionIds.length > 0) {
          const retryQs = allQuestions.filter(q => retryQuestionIds.includes(q.id));
          const normalQs = allQuestions.filter(q => !retryQuestionIds.includes(q.id));
          const mixed = [];
          let ri = 0, ni = 0;
          while (ri < retryQs.length || ni < normalQs.length) {
            if (ri < retryQs.length) mixed.push(retryQs[ri++]);
            if (ni < normalQs.length) mixed.push(normalQs[ni++]);
          }
          allQuestions = mixed;
        }

        setQuestions(allQuestions);
        setCurrentIndex(0);
        setSelectedAnswer(null);
        setResult(null);
        setStats({ correct: 0, incorrect: 0, total: 0 });
        setScore(0);
        setHintCount(0);
        setQuizFinished(false);
        retryQueueRef.current = [];
        setRetryQueueLength(0);
        hintUsedRef.current = new Set();
        // Initialiser les compteurs retry depuis la BDD
        retryCountersRef.current = new Map(retryScoresMap);
        // Initialiser le Set des questions ratées avec les retry BDD
        failedQuestionIdsRef.current = new Set(retryQuestionIds);
        setShowExercise(true);
        setChrono(0);
        startChrono();
      } else {
        setError(data.error || 'Aucune question disponible');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerClick = async (answerIndex) => {
    if (result || isChecking || showHintModal) return;
    setSelectedAnswer(answerIndex);
    setIsChecking(true);

    try {
      const currentQ = questions[currentIndex];
      const response = await fetch('/api/exercices/quiz-grammaire/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          questionId: currentQ.id,
          userAnswerIndex: answerIndex,
          userId: user?.id,
        }),
      });
      const data = await response.json();

      if (data.success) {
        setResult(data);

        const isCorrect = data.correct;
        const isHinted = hintUsedRef.current.has(currentQ.id);

        // === SCORE AFFICHÉ (séparé du retry) ===
        if (isHinted) {
          // Après hint : score et stats totalement neutres
        } else {
          const pointsDelta = isCorrect ? 1 : -1;
          setScore(prev => prev + pointsDelta);
          setStats(prev => ({
            correct: prev.correct + (isCorrect ? 1 : 0),
            incorrect: prev.incorrect + (isCorrect ? 0 : 1),
            total: prev.total + 1,
          }));
        }

        // === COMPTEUR RETRY (toujours mis à jour, même après hint) ===
        const currentCounter = retryCountersRef.current.get(currentQ.id) || 0;
        let retryDelta;
        if (isCorrect) {
          retryDelta = 1;
        } else {
          // -2 pour la première erreur, -1 pour les suivantes
          retryDelta = failedQuestionIdsRef.current.has(currentQ.id) ? -1 : -2;
          failedQuestionIdsRef.current.add(currentQ.id);
        }
        const newCounter = currentCounter + retryDelta;
        retryCountersRef.current.set(currentQ.id, newCounter);

        // Si compteur < 1 et réponse incorrecte : ajouter au pool de retry
        if (newCounter < 1 && !isCorrect) {
          addToRetryQueue(currentQ);
        }

        // === PERSISTANCE BDD ===
        // Toujours persister en BDD (correct, incorrect) — sauf si jamais touché
        if (!isCorrect || failedQuestionIdsRef.current.has(currentQ.id) || isHinted) {
          updateScoreBDD(currentQ.id, isCorrect ? 'correct' : 'incorrect');
        }
      }
    } catch (err) {
      console.error('Erreur vérification:', err);
    } finally {
      setIsChecking(false);
    }
  };

  const handleNext = () => {
    // Fin du quiz si on a atteint le nombre de questions demandé
    if (stats.total >= quantite) {
      stopChrono();
      saveSessionEnd(stats, score, chrono, hintCount);
      setQuizFinished(true);
      return;
    }

    // Vérifier s'il y a une question retry prête
    const retryQuestion = processRetryQueue();

    if (retryQuestion) {
      // Insérer la question retry juste après la position actuelle
      setQuestions(prev => {
        const newQuestions = [...prev];
        newQuestions.splice(currentIndex + 1, 0, retryQuestion);
        return newQuestions;
      });
    }

    if (retryQuestion || currentIndex < questions.length - 1) {
      // Soit une retry a été insérée, soit il reste des questions normales
      setCurrentIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setResult(null);
      setShowRule(false);
    } else {
      stopChrono();
      saveSessionEnd(stats, score, chrono, hintCount);
      setQuizFinished(true);
    }
  };

  const handleRestart = () => {
    stopChrono();
    setShowExercise(false);
    setQuestions([]);
    setCurrentIndex(0);
    setResult(null);
    setStats({ correct: 0, incorrect: 0, total: 0 });
    setScore(0);
    setQuizFinished(false);
    retryQueueRef.current = [];
    setRetryQueueLength(0);
    failedQuestionIdsRef.current = new Set();
    hintUsedRef.current = new Set();
    retryCountersRef.current = new Map();
    setHintCount(0);
    setChrono(0);
  };

  // ========== "JE NE SAIS PAS" ==========
  const handleDontKnow = async () => {
    const currentQ = questions[currentIndex];
    if (!currentQ || result) return;

    // Marquer la question comme "hintée" (score neutre pour la réponse suivante)
    hintUsedRef.current.add(currentQ.id);
    setHintCount(prev => prev + 1);
    // Le hint compte dans le total de questions
    setStats(prev => ({ ...prev, total: prev.total + 1 }));
    // Compteur retry local : -1 pour hint
    const currentCounter = retryCountersRef.current.get(currentQ.id) || 0;
    retryCountersRef.current.set(currentQ.id, currentCounter - 1);
    // Persister en BDD
    updateScoreBDD(currentQ.id, 'hint');

    setLoadingHint(true);
    try {
      const response = await fetch('/api/exercices/quiz-grammaire/hint', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questionId: currentQ.id }),
      });
      const data = await response.json();

      if (data.success) {
        setHintData(data);
        setShowHintModal(true);
      }
    } catch (err) {
      console.error('Erreur hint:', err);
    } finally {
      setLoadingHint(false);
    }
  };

  const closeHintModal = () => {
    setShowHintModal(false);
    setHintData(null);
    // On revient à la même question — rien d'autre à faire
  };

  // ========== SIGNALER ==========
  const handleSignaler = async () => {
    const currentQ = questions[currentIndex];
    if (!currentQ) return;

    const confirmation = window.confirm(t('grammarQuiz.confirmSignaler'));
    if (!confirmation) return;

    // Annuler l'impact de la réponse sur le score et les stats
    // (la question signalée ne doit pas compter, et on ajoute une question supplémentaire)
    const isHinted = hintUsedRef.current.has(currentQ.id);
    if (!isHinted && result) {
      // Inverser le score affiché (+1 → -1, -1 → +1)
      const wasCorrect = result.correct;
      setScore(prev => prev + (wasCorrect ? -1 : 1));
      // Inverser les stats
      setStats(prev => ({
        correct: prev.correct - (wasCorrect ? 1 : 0),
        incorrect: prev.incorrect - (wasCorrect ? 0 : 1),
        total: prev.total - 1,
      }));
    } else if (isHinted) {
      // Hint : le total avait été incrémenté dans handleDontKnow → décrémenter
      setStats(prev => ({ ...prev, total: prev.total - 1 }));
      setHintCount(prev => prev - 1);
    }

    try {
      const response = await fetch(`/api/exercices/quiz-grammaire/${currentQ.id}/signaler`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();

      if (data.success) {
        handleNext();
      } else {
        throw new Error(data.error || 'Erreur lors du signalement');
      }
    } catch (err) {
      console.error('Erreur signalement:', err);
      alert(t('grammarQuiz.erreurSignalement') + err.message);
    }
  };

  const successRate = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;
  const currentQuestion = questions[currentIndex];

  // ========== RENDU RÈGLE SOMMAIRE ==========
  const renderRegleSommaire = (regleSommaire) => {
    if (!regleSommaire) return null;
    if (typeof regleSommaire === 'string') {
      return <p className="rule-text">{regleSommaire}</p>;
    }
    return (
      <>
        {regleSommaire.general_rule && (
          <p className="rule-text">
            {typeof regleSommaire.general_rule === 'string'
              ? regleSommaire.general_rule
              : JSON.stringify(regleSommaire.general_rule)}
          </p>
        )}
        {Array.isArray(regleSommaire.examples) && regleSommaire.examples.length > 0 && (
          <div className="rule-examples">
            <strong>{t('grammarQuiz.exemples')}</strong>
            <ul>
              {regleSommaire.examples.map((ex, i) => (
                <li key={i}>{typeof ex === 'string' ? ex : JSON.stringify(ex)}</li>
              ))}
            </ul>
          </div>
        )}
      </>
    );
  };

  // ========== MODALE "JE NE SAIS PAS" ==========
  const renderHintModal = () => {
    if (!showHintModal || !hintData) return null;
    return (
      <div className="hint-modal-overlay" onClick={closeHintModal}>
        <div className="hint-modal" onClick={e => e.stopPropagation()}>
          <button className="hint-modal-close" onClick={closeHintModal}>
            <X size={20} />
          </button>
          <h3>{t('grammarQuiz.laBonneReponse')}</h3>
          <p className="hint-answer">{hintData.reponseCorrecte}</p>
          {hintData.explication && (
            <div className="hint-explication">
              <p>{hintData.explication}</p>
            </div>
          )}
          {hintData.regleSommaire && (
            <div className="hint-rule">
              <strong><BookOpen size={14} /> {t('grammarQuiz.regle')}</strong>
              {renderRegleSommaire(hintData.regleSommaire)}
            </div>
          )}
          <button className="btn-hint-understood" onClick={closeHintModal}>
            {t('grammarQuiz.jaiCompris')}
          </button>
        </div>
      </div>
    );
  };

  // ========== HELPERS AFFICHAGE ==========
  const getNiveauUserLabel = (niv) => {
    const labels = ['A1', 'A1+', 'A2', 'A2+', 'B1', 'B1+', 'B2', 'B2+', 'C1', 'C2'];
    return labels[niv] || `${niv}`;
  };

  const getNiveauExerciceLabel = (niv) => {
    if (!niv || niv === 'all') return t('grammarQuiz.niveauTous');
    const labels = { beginner: t('grammarQuiz.niveauBeginner'), intermediate: t('grammarQuiz.niveauIntermediate'), advanced: t('grammarQuiz.niveauAdvanced') };
    return labels[niv] || niv;
  };

  // ========== MODALE FIN DE QUIZ ==========
  if (showExercise && quizFinished) {
    return (
      <div className="grammar-quiz-exercise">
        <div className="finished-modal-overlay">
          <div className="finished-modal">
            <h2>{t('grammarQuiz.quizTermine')}</h2>

            <div className="final-message">
              {successRate >= 80 && <p>{t('grammarQuiz.messageExcellent')}</p>}
              {successRate >= 50 && successRate < 80 && <p>{t('grammarQuiz.messageBon')}</p>}
              {successRate < 50 && <p>{t('grammarQuiz.messageContinuez')}</p>}
            </div>

            <div className="final-stats">
              <div className="final-stat score-stat">
                <span className="final-stat-value">{score}</span>
                <span className="final-stat-label">{t('grammarQuiz.score')}</span>
              </div>
              <div className="final-stat correct">
                <CheckCircle size={32} />
                <span className="final-stat-value">{stats.correct}</span>
                <span className="final-stat-label">{t('grammarQuiz.correctes')}</span>
              </div>
              <div className="final-stat incorrect">
                <XCircle size={32} />
                <span className="final-stat-value">{stats.incorrect}</span>
                <span className="final-stat-label">{t('grammarQuiz.incorrectes')}</span>
              </div>
              {hintCount > 0 && (
                <div className="final-stat hint">
                  <HelpCircle size={32} />
                  <span className="final-stat-value">{hintCount}</span>
                  <span className="final-stat-label">{t('grammarQuiz.jeNeSaisPas')}</span>
                </div>
              )}
              <div className="final-stat rate">
                <TrendingUp size={32} />
                <span className="final-stat-value">{successRate}%</span>
                <span className="final-stat-label">{t('grammarQuiz.reussite')}</span>
              </div>
            </div>

            <div className="final-details">
              <div className="final-detail-row">
                <Clock size={16} />
                <span className="detail-label">{t('grammarQuiz.temps')}</span>
                <span className="detail-value">{formatChrono(chrono)}</span>
              </div>
              <div className="final-detail-row">
                <Brain size={16} />
                <span className="detail-label">{t('grammarQuiz.questions')}</span>
                <span className="detail-value">{stats.total}</span>
              </div>
              <div className="final-detail-row">
                <span className="detail-icon">{getLangueFlag(langue)}</span>
                <span className="detail-label">{t('grammarQuiz.niveauExercice')}</span>
                <span className="detail-value">{getNiveauExerciceLabel(niveau)}</span>
              </div>
              {user?.niveau != null && (
                <div className="final-detail-row">
                  <TrendingUp size={16} />
                  <span className="detail-label">{t('grammarQuiz.votreNiveau')}</span>
                  <span className="detail-value">{getNiveauUserLabel(user.niveau)}</span>
                </div>
              )}
            </div>

            <div className="final-actions">
              <button className="btn-restart" onClick={startQuiz}>
                <RotateCw size={18} />
                {t('grammarQuiz.rejouer')}
              </button>
              <button className="btn-back-settings" onClick={handleRestart}>
                {t('grammarQuiz.changerParametres')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========== ÉCRAN EXERCICE ==========
  if (showExercise && currentQuestion) {
    return (
      <div className="grammar-quiz-exercise">
        {renderHintModal()}
        <div className="exercise-zone">
          {/* Header : progression, stats, chrono, score */}
          <div className="quiz-header">
            <div className="quiz-progress">
              <span className="progress-text">
                {currentIndex + 1} / {questions.length}
              </span>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
                />
              </div>
            </div>
            <div className="stats-compact">
              <span className="stat-compact chrono">
                <Clock size={16} />
                {formatChrono(chrono)}
              </span>
              <span className="stat-compact score-badge" title="Score">
                {score >= 0 ? '+' : ''}{score}
              </span>
              <span className="stat-compact correct">
                <CheckCircle size={16} />
                {stats.correct}
              </span>
              <span className="stat-compact incorrect">
                <XCircle size={16} />
                {stats.incorrect}
              </span>
              {hintCount > 0 && (
                <span className="stat-compact hint-count" title="Je ne sais pas">
                  <HelpCircle size={16} />
                  {hintCount}
                </span>
              )}
              <span className="stat-compact rate">
                <TrendingUp size={16} />
                {successRate}%
              </span>
              {retryQueueLength > 0 && (
                <span className="stat-compact retry" title="Questions en attente de révision">
                  🔄 {retryQueueLength}
                </span>
              )}
            </div>
          </div>

          {/* Carte question */}
          <div className="quiz-card">
            <div className="quiz-theme-badge">
              {getLangueFlag(langue)} {currentQuestion.pointGrammaire}
            </div>

            <p className="quiz-question-text">{currentQuestion.question}</p>

            {/* Bouton "Je ne sais pas" */}
            {!result && (
              <button
                className="btn-dont-know"
                onClick={handleDontKnow}
                disabled={loadingHint || isChecking}
              >
                <HelpCircle size={16} />
                {loadingHint ? t('grammarQuiz.chargement') : t('grammarQuiz.jeNeSaisPas')}
              </button>
            )}

            {/* Boutons réponses */}
            <div className="quiz-answers">
              {currentQuestion.reponses.map((reponse, index) => {
                let btnClass = 'quiz-answer-btn';
                if (result) {
                  if (index === result.indexCorrect) {
                    btnClass += ' correct';
                  } else if (index === selectedAnswer && !result.correct) {
                    btnClass += ' incorrect';
                  } else {
                    btnClass += ' disabled';
                  }
                } else if (index === selectedAnswer) {
                  btnClass += ' selected';
                }

                return (
                  <button
                    key={index}
                    className={btnClass}
                    onClick={() => handleAnswerClick(index)}
                    disabled={!!result || isChecking || showHintModal}
                  >
                    <span className="answer-letter">{String.fromCharCode(65 + index)}</span>
                    <span className="answer-text">{reponse}</span>
                  </button>
                );
              })}
            </div>

            {/* Résultat et explication */}
            {result && (
              <div className={`quiz-result ${result.correct ? 'correct' : 'incorrect'}`}>
                <div className="result-header">
                  {result.correct ? (
                    <>
                      <CheckCircle size={20} />
                      <span>{t('grammarQuiz.bonneReponse')}</span>
                    </>
                  ) : (
                    <>
                      <XCircle size={20} />
                      <span>
                        {t('grammarQuiz.incorrectReponse')} <strong>{result.reponseCorrecte}</strong>
                      </span>
                    </>
                  )}
                </div>

                <p className="quiz-explication">{result.explication}</p>

                {/* Bouton voir la règle */}
                {result.regleSommaire && (
                  <>
                    <button className="btn-show-rule" onClick={() => setShowRule(!showRule)}>
                      <BookOpen size={16} />
                      {showRule ? t('grammarQuiz.masquerRegle') : t('grammarQuiz.voirRegle')}
                    </button>

                    {showRule && (
                      <div className="rule-summary">
                        {renderRegleSommaire(result.regleSommaire)}
                      </div>
                    )}
                  </>
                )}

                {/* Bouton suivant */}
                <div className="quiz-result-actions">
                  <button className="btn-next-question" onClick={handleNext}>
                    {stats.total < quantite && (currentIndex < questions.length - 1 || retryQueueRef.current.length > 0) ? (
                      <>
                        {t('grammarQuiz.questionSuivante')}
                        <ChevronRight size={18} />
                      </>
                    ) : (
                      t('grammarQuiz.voirResultats')
                    )}
                  </button>
                  <button className="btn-signaler" onClick={handleSignaler}>
                    {t('grammarQuiz.signalerErreur')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Bouton retour paramètres */}
          <button className="btn-back-to-settings" onClick={handleRestart}>
            {t('grammarQuiz.retourParametres')}
          </button>
        </div>
      </div>
    );
  }

  // ========== ÉCRAN PARAMÈTRES ==========
  return (
    <div className="grammar-quiz-exercise">
      <div className="settings-screen">
        {!isEmbed && (
          <div className="exercise-header">
            <div className="header-title">
              <Brain size={32} />
              <h2>{t('grammarQuiz.title')}</h2>
            </div>
          </div>
        )}
        <div className="settings-container">

          {/* Sélecteur de langue */}
          <div className="setting-item">
            <label>{t('grammarQuiz.langue')}</label>
            <select
              className="language-select"
              value={langue}
              onChange={(e) => setLangue(e.target.value)}
            >
              {langueOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.flag} {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Sélecteur de générique */}
          <div className="setting-item">
            <label>{t('grammarQuiz.theme')}</label>
            {loadingGeneriques ? (
              <div className="loading-themes">
                <Loader2 size={16} className="spin" /> {t('grammarQuiz.chargement')}
              </div>
            ) : (
              <select
                className="language-select"
                value={generiqueId}
                onChange={(e) => setGeneriqueId(e.target.value)}
              >
                <option value="all">
                  {t('grammarQuiz.tousLesThemes', { count: generiques.reduce((sum, g) => sum + g.nbQuestions, 0) })}
                </option>
                {generiques.map((g) => (
                  <option key={g.id} value={g.id}>
                    {t('grammarQuiz.themeOption', { generique: g.generique, count: g.nbQuestions })}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Sélecteur de niveau */}
          <div className="setting-item">
            <label>{t('grammarQuiz.niveau')}</label>
            <select
              className="language-select"
              value={niveau}
              onChange={(e) => setNiveau(e.target.value)}
            >
              {niveauOptions.map((opt) => {
                const count = niveauCounts[opt.value];
                return (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}{count !== undefined ? t('grammarQuiz.niveauOptionCount', { count }) : ''}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Sélecteur de quantité */}
          <div className="setting-item">
            <label>{t('grammarQuiz.nombreQuestions')}</label>
            <select
              className="language-select"
              value={quantite}
              onChange={(e) => setQuantite(parseInt(e.target.value))}
            >
              {quantiteOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Erreur */}
          {error && (
            <div className="quiz-error">
              <XCircle size={16} />
              {error}
            </div>
          )}

          {/* Bouton commencer */}
          <button
            className="btn-start-exercise"
            onClick={startQuiz}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 size={18} className="spin" />
                {t('grammarQuiz.chargement')}
              </>
            ) : (
              <>
                <Play size={18} />
                {t('grammarQuiz.start')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
